import { useUpdateAccountPropertiesMutation } from "@api/accounts";
import { useAppDispatch } from "@store";
import { setDialogId } from "@store/global-slice";
import debounce from "lodash.debounce";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { cn } from "@/lib/cn";
import { Button } from "@/shared";
import { useCurrentAccount } from "../providers/current-account-provider";
import { getFontsTemplate, validateFonts } from "../utils";

export const CustomFonts = () => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const { account, isDifferentFromCurrentAccount } = useCurrentAccount();
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const [value, setValue] = useState<string>();
  const [updateAccountProperties, { isLoading }] =
    useUpdateAccountPropertiesMutation();

  const onSubmit = async () => {
    setError(null);
    const { error, isValid } = validateFonts(value);
    if (!isValid) return setError(error);

    try {
      const parsed = value ? JSON.parse(value) : null;
      await updateAccountProperties({
        type: "fonts",
        payload: parsed,
        id: account?._id as string,
      }).unwrap();
      if (isDifferentFromCurrentAccount) {
        toast.success(`${account?.title} fonts updated successfully`);
      } else {
        toast.success("Fonts updated successfully");
        window.location.reload();
      }
    } catch (error) {
      toast.error("Failed to update fonts");
    }
    dispatch(setDialogId({ id: null }));
  };

  const handleContentChange = debounce((value: string) => {
    setValue(value);
  }, 500);

  useEffect(() => {
    if (account?.fonts?.custom) {
      ref.current!.value = JSON.stringify(account.fonts.custom, null, 2);
    }
  }, [account]);

  return (
    <div>
      <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base">
        Custom fonts
      </span>
      <textarea
        ref={ref}
        onInput={(e) => handleContentChange(e.currentTarget.value)}
        className={cn(
          "scrollbar tw-flex tw-h-24 tw-w-full tw-resize-none tw-items-center tw-justify-start tw-rounded tw-border tw-border-solid tw-border-[#e7e6ec] tw-bg-white tw-px-3 tw-py-1 tw-font-sans tw-text-xs tw-font-normal tw-text-blue-base placeholder:tw-text-[#88819f] focus:tw-outline focus:tw-outline-2 focus:tw-outline-offset-2 focus:tw-outline-[#7b17cf33]",
          {
            "tw-border-red-200 tw-bg-red-50/50": false,
          },
        )}
      ></textarea>
      {error && (
        <span className="tw-mt-2 tw-block tw-text-[10px]/[12px] tw-font-semibold tw-text-red-base">
          {error}
        </span>
      )}
      <div className="tw-mt-6 tw-flex tw-items-center tw-gap-2">
        <Button
          size="large"
          variant="secondary"
          title="Update fonts"
          loading={isLoading}
          onClick={onSubmit}
        />
        <Button
          size="large"
          variant="link"
          title="Get fonts template"
          onClick={getFontsTemplate}
        />
      </div>
    </div>
  );
};
